<template>
  <div class="gps-body">
    <div class="gps-header">
      <div>
        <p class="gps-header__title">Permissions</p>
        <p class="gps-header__sub">Presets are shortcuts — assigned permissions are the actual source of truth.</p>
      </div>
      <span class="gps-count">{{ normalizedSelection.length }} selected</span>
    </div>

    <div class="gps-presets">
      <div class="gps-presets__header">
        <p class="gps-presets__title">Quick presets</p>
        <button
          type="button"
          class="gps-clear-btn"
          :disabled="disabled || !normalizedSelection.length"
          @click="clearSelection"
        >
          Clear
        </button>
      </div>
      <div class="gps-preset-grid">
        <button
          v-for="preset in presetEntries"
          :key="preset.name"
          type="button"
          class="gps-preset-btn"
          :class="{ 'is-active': isPresetActive(preset.permissions) }"
          :disabled="disabled"
          @click="applyPreset(preset.permissions)"
        >
          <span class="gps-preset-btn__name">{{ formatRoleLabel(preset.name) }}</span>
          <span class="gps-preset-btn__count">{{ preset.permissions.length }}</span>
          <span class="gps-preset-btn__desc">{{ presetSummary(preset.permissions) }}</span>
        </button>
      </div>
    </div>

    <div class="gps-perms">
      <p class="gps-perms__title">All permissions</p>
      <div v-if="loading" class="gps-perms__loading">Loading permissions…</div>
      <div v-else class="gps-perms__list">
        <hs-checkbox
          v-for="permission in allPermissions"
          :key="permission"
          :model-value="normalizedSelection.includes(permission)"
          :label="formatPermissionLabel(permission)"
          :description="permission"
          :disabled="disabled"
          @update:modelValue="togglePermission(permission)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from "vue";
import { HsCheckbox } from "@/components/ui";

const ROLE_PRIORITY = ["ADMIN", "MEMBER", "READ_ONLY"];

export default {
  name: "GroupPermissionsSelector",
  components: { HsCheckbox },
  props: {
    modelValue: { type: Array, default: () => [] },
    catalog: { type: Object, default: () => ({}) },
    loading: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
  },
  emits: ["update:modelValue"],
  setup(props, { emit }) {
    const normalizedSelection = computed(() =>
      Array.from(new Set((props.modelValue || []).map((p) => String(p).trim().toUpperCase()).filter(Boolean))).sort()
    );

    const presetEntries = computed(() =>
      ROLE_PRIORITY.filter((name) => Array.isArray(props.catalog?.[name])).map((name) => ({
        name,
        permissions: Array.from(new Set(props.catalog[name].map((p) => String(p).trim().toUpperCase()).filter(Boolean))).sort(),
      }))
    );

    const allPermissions = computed(() => {
      const set = new Set();
      presetEntries.value.forEach((preset) => preset.permissions.forEach((p) => set.add(p)));
      return Array.from(set).sort();
    });

    const emitSelection = (permissions) => {
      emit("update:modelValue", Array.from(new Set((permissions || []).map((p) => String(p).trim().toUpperCase()).filter(Boolean))).sort());
    };

    const togglePermission = (permission) => {
      const selection = new Set(normalizedSelection.value);
      if (selection.has(permission)) selection.delete(permission); else selection.add(permission);
      emitSelection(Array.from(selection));
    };

    const applyPreset = (permissions) => { emitSelection(permissions); };
    const clearSelection = () => { emitSelection([]); };

    const isPresetActive = (permissions) => {
      if (normalizedSelection.value.length !== permissions.length) return false;
      const selection = new Set(normalizedSelection.value);
      return permissions.every((p) => selection.has(p));
    };

    const formatRoleLabel = (name) =>
      String(name).toLowerCase().split("_").filter(Boolean).map((s) => s[0].toUpperCase() + s.slice(1)).join(" ");

    const formatPermissionLabel = (name) =>
      String(name).replace(/^CAN_/, "").toLowerCase().split("_").filter(Boolean).map((s) => s[0].toUpperCase() + s.slice(1)).join(" ");

    const presetSummary = (permissions) =>
      permissions.slice(0, 2).map(formatPermissionLabel).join(", ");

    return {
      normalizedSelection, presetEntries, allPermissions,
      togglePermission, applyPreset, clearSelection, isPresetActive,
      formatRoleLabel, formatPermissionLabel, presetSummary,
    };
  },
};
</script>

<style scoped>
.gps-body {
  display: grid;
  gap: 1rem;
}

.gps-header {
  align-items: flex-start;
  display: flex;
  gap: 0.75rem;
  justify-content: space-between;
}

.gps-header__title {
  color: var(--hs-text);
  font-size: 0.88rem;
  font-weight: 700;
  margin: 0;
}

.gps-header__sub {
  color: var(--hs-muted);
  font-size: 0.76rem;
  margin: 0.15rem 0 0;
}

.gps-count {
  color: var(--hs-muted);
  flex-shrink: 0;
  font-size: 0.75rem;
  font-weight: 600;
  padding-top: 0.1rem;
}

.gps-presets__header {
  align-items: center;
  display: flex;
  gap: 0.5rem;
  justify-content: space-between;
  margin-bottom: 0.6rem;
}

.gps-presets__title {
  color: var(--hs-text);
  font-size: 0.82rem;
  font-weight: 650;
  margin: 0;
}

.gps-clear-btn {
  background: none;
  border: none;
  color: var(--hs-primary);
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0;
}

.gps-clear-btn:disabled {
  color: var(--hs-muted);
  cursor: not-allowed;
  opacity: 0.5;
}

.gps-preset-grid {
  display: grid;
  gap: 0.5rem;
  grid-template-columns: repeat(3, 1fr);
}

@media (max-width: 480px) {
  .gps-preset-grid { grid-template-columns: 1fr; }
}

.gps-preset-btn {
  background: color-mix(in srgb, var(--hs-bg-soft) 80%, transparent);
  border: 2px solid var(--hs-border);
  border-radius: 10px;
  color: var(--hs-text);
  cursor: pointer;
  display: grid;
  gap: 0.2rem;
  padding: 0.65rem 0.75rem;
  text-align: left;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.gps-preset-btn:hover:not(:disabled) {
  border-color: color-mix(in srgb, var(--hs-primary) 40%, transparent);
}

.gps-preset-btn.is-active {
  background: color-mix(in srgb, var(--hs-primary) 8%, transparent);
  border-color: var(--hs-primary);
}

.gps-preset-btn:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.gps-preset-btn__name {
  color: var(--hs-text);
  font-size: 0.83rem;
  font-weight: 700;
}

.gps-preset-btn.is-active .gps-preset-btn__name {
  color: var(--hs-primary);
}

.gps-preset-btn__count {
  color: var(--hs-muted);
  font-size: 0.72rem;
}

.gps-preset-btn__desc {
  color: var(--hs-muted);
  font-size: 0.72rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gps-perms__title {
  color: var(--hs-text);
  font-size: 0.82rem;
  font-weight: 650;
  margin: 0 0 0.6rem;
}

.gps-perms__loading {
  background: color-mix(in srgb, var(--hs-bg-soft) 80%, transparent);
  border: 1px solid var(--hs-border);
  border-radius: 10px;
  color: var(--hs-muted);
  font-size: 0.83rem;
  padding: 0.85rem;
}

.gps-perms__list {
  background: color-mix(in srgb, var(--hs-bg-soft) 50%, transparent);
  border: 1px solid var(--hs-border);
  border-radius: 10px;
  display: grid;
  gap: 0.1rem;
  grid-template-columns: 1fr 1fr;
  padding: 0.65rem 0.75rem;
}

@media (max-width: 480px) {
  .gps-perms__list { grid-template-columns: 1fr; }
}
</style>
